'use client'

import React, { useState } from 'react'
import { cn } from '@/lib/utils'
import { Icons } from '@/components/ui/icons'
import { Avatar } from '@/components/ui/avatar'
import { useAI } from '@/context/AIContext'
import apiClient from '@/lib/api-client'
import type { ChatMessage as ChatMessageType, ToolCall } from '@/context/AIContext'

interface ChatMessageProps {
  message: ChatMessageType
  userName?: string
  userImage?: string | null
}

/** Mutation tool names that require user confirmation. */
const MUTATION_TOOLS = new Set(['resolve_exception', 'escalate_exception', 'toggle_policy'])

function formatTime(date: Date): string {
  return date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  })
}

/**
 * Render inline markdown (bold, code, italic, links) in text.
 *
 * Links:  [label](url)
 *   - Report download links (/api/reports/) → premium download button
 *   - Other links → styled inline link with external-link icon
 *   - Relative backend URLs are prefixed with NEXT_PUBLIC_API_URL
 */
function renderInlineMarkdown(text: string): React.ReactNode[] {
  const parts: React.ReactNode[] = []
  // Match [label](url), **bold**, `code`, and *italic*
  const regex = /(\[[^\]]+\]\([^)]+\))|(\*\*[^*]+\*\*)|(`[^`]+`)|(\*[^*]+\*)/g
  let lastIndex = 0
  let match
  let keyIndex = 0

  /** Resolve relative backend paths to full URLs. */
  const resolveUrl = (url: string): string => {
    if (url.startsWith('/api/')) {
      const base = process.env.NEXT_PUBLIC_API_URL || 'http://localhost:8001'
      return `${base}${url}`
    }
    return url
  }

  while ((match = regex.exec(text)) !== null) {
    // Add text before match
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index))
    }

    const segment = match[0]

    // Markdown link: [label](url)
    if (segment.startsWith('[')) {
      const linkMatch = segment.match(/^\[([^\]]+)\]\(([^)]+)\)$/)
      if (linkMatch) {
        const label = linkMatch[1]
        const href = resolveUrl(linkMatch[2])
        const isDownload = linkMatch[2].includes('/api/reports/')

        if (isDownload) {
          // Premium branded download button
          parts.push(
            <a
              key={keyIndex++}
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 mt-1.5 mb-0.5 px-4 py-2 rounded-lg
                bg-gradient-to-r from-brand-navy to-brand-purple
                text-white text-xs font-semibold
                shadow-md hover:shadow-lg
                transition-all duration-200 hover:scale-[1.02] active:scale-[0.98]
                no-underline"
            >
              <Icons.download className="h-3.5 w-3.5" strokeWidth={2} />
              {label}
            </a>
          )
        } else {
          // Standard styled link
          parts.push(
            <a
              key={keyIndex++}
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-brand-cornflower hover:text-brand-navy
                font-medium underline underline-offset-2 transition-colors"
            >
              {label}
              <Icons.externalLink className="h-3 w-3 inline-block" strokeWidth={2} />
            </a>
          )
        }
      }
    } else if (segment.startsWith('**') && segment.endsWith('**')) {
      // Bold
      parts.push(
        <strong key={keyIndex++} className="font-semibold text-foreground">
          {segment.slice(2, -2)}
        </strong>
      )
    } else if (segment.startsWith('`') && segment.endsWith('`')) {
      // Code
      parts.push(
        <code
          key={keyIndex++}
          className="px-1.5 py-0.5 bg-muted/50 rounded text-xs font-mono text-brand-navy"
        >
          {segment.slice(1, -1)}
        </code>
      )
    } else if (segment.startsWith('*') && segment.endsWith('*')) {
      // Italic
      parts.push(
        <em key={keyIndex++} className="italic">
          {segment.slice(1, -1)}
        </em>
      )
    }
    lastIndex = regex.lastIndex
  }

  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex))
  }

  return parts.length > 0 ? parts : [text]
}

/**
 * Render markdown content with support for headers, lists, blockquotes
 */
function MarkdownContent({ content }: { content: string }) {
  const lines = content.split('\n')
  
  return (
    <div className="space-y-2 text-sm leading-relaxed">
      {lines.map((line, i) => {
        const trimmedLine = line.trim()
        
        // Empty line - add spacing
        if (!trimmedLine) {
          return <div key={i} className="h-1" />
        }
        
        // Headers (##, ###)
        if (trimmedLine.startsWith('### ')) {
          return (
            <h4 key={i} className="font-semibold text-foreground mt-3 first:mt-0">
              {renderInlineMarkdown(trimmedLine.slice(4))}
            </h4>
          )
        }
        if (trimmedLine.startsWith('## ')) {
          return (
            <h3 key={i} className="font-bold text-foreground mt-3 first:mt-0">
              {renderInlineMarkdown(trimmedLine.slice(3))}
            </h3>
          )
        }
        
        // Blockquote
        if (trimmedLine.startsWith('> ')) {
          return (
            <blockquote
              key={i}
              className="pl-3 border-l-2 border-brand-cornflower/50 text-muted-foreground italic"
            >
              {renderInlineMarkdown(trimmedLine.slice(2))}
            </blockquote>
          )
        }
        
        // Bullet points (* or -)
        if (/^[*\-]\s/.test(trimmedLine)) {
          return (
            <div key={i} className="flex gap-2 pl-1">
              <span className="w-1.5 h-1.5 rounded-full bg-brand-cornflower/60 mt-2 flex-shrink-0" />
              <span>{renderInlineMarkdown(trimmedLine.replace(/^[*\-]\s/, ''))}</span>
            </div>
          )
        }
        
        // Numbered lists
        if (/^\d+\.\s/.test(trimmedLine)) {
          const match = trimmedLine.match(/^(\d+)\.\s(.*)/)
          if (match) {
            return (
              <div key={i} className="flex gap-2 pl-1">
                <span className="text-brand-cornflower font-medium min-w-[1.25rem]">
                  {match[1]}.
                </span>
                <span>{renderInlineMarkdown(match[2])}</span>
              </div>
            )
          }
        }
        
        // Regular paragraph
        return <p key={i}>{renderInlineMarkdown(trimmedLine)}</p>
      })}
    </div>
  )
}


// ============================================================================
// Confirmation Card — Human-in-the-Loop for mutation tool calls
// ============================================================================

interface ConfirmationCardProps {
  toolCall: ToolCall
}

function ConfirmationCard({ toolCall }: ConfirmationCardProps) {
  const { addMessage, fireMutationComplete } = useAI()
  const [status, setStatus] = useState<'pending' | 'confirming' | 'confirmed' | 'cancelled'>('pending')

  const result = toolCall.result as Record<string, unknown> | undefined
  if (!result || !result.requires_confirmation) return null

  const summary = (result.summary as string) || 'Perform action'
  const action = result.action as string
  const mutationType = (result.mutation_type as string) || 'data_changed'

  const handleConfirm = async () => {
    setStatus('confirming')
    try {
      // Execute the actual mutation via API
      if (action === 'resolve_exception') {
        await apiClient(`/api/workbench/exceptions/${result.exception_id}/resolve`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ resolution_note: result.resolution_note }),
        })
      } else if (action === 'escalate_exception') {
        await apiClient(`/api/workbench/exceptions/${result.exception_id}/escalate`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ reason: result.reason }),
        })
      } else if (action === 'toggle_policy') {
        await apiClient(`/api/ai/policies/${result.policy_id}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ is_active: result.is_active }),
        })
      }

      setStatus('confirmed')
      addMessage({
        role: 'assistant',
        content: `✅ **Action completed:** ${summary}`,
      })

      // Notify listening pages to refresh their data
      fireMutationComplete(mutationType)
    } catch {
      setStatus('pending')
      addMessage({
        role: 'assistant',
        content: `❌ **Action failed.** Could not complete: ${summary}. Please try again or perform the action manually.`,
      })
    }
  }

  const handleCancel = () => {
    setStatus('cancelled')
    addMessage({
      role: 'assistant',
      content: `Action cancelled: ${summary}`,
    })
  }

  if (status === 'confirmed') {
    return (
      <div className="mt-2 flex items-center gap-2 rounded-lg bg-emerald-50 border border-emerald-200 px-3 py-2 text-xs text-emerald-700">
        <Icons.checkCircle className="h-4 w-4 flex-shrink-0" strokeWidth={1.5} />
        <span className="font-medium">Confirmed</span>
      </div>
    )
  }

  if (status === 'cancelled') {
    return (
      <div className="mt-2 flex items-center gap-2 rounded-lg bg-gray-50 border border-gray-200 px-3 py-2 text-xs text-muted-foreground">
        <Icons.xCircle className="h-4 w-4 flex-shrink-0" strokeWidth={1.5} />
        <span>Cancelled</span>
      </div>
    )
  }

  return (
    <div className="mt-3 rounded-xl border border-amber-200/80 bg-gradient-to-r from-amber-50 to-orange-50 p-3 shadow-soft">
      <div className="flex items-start gap-2 mb-3">
        <Icons.alertTriangle className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" strokeWidth={1.5} />
        <div className="flex-1 min-w-0">
          <p className="text-xs font-semibold text-amber-800">Confirmation Required</p>
          <p className="text-xs text-amber-700 mt-0.5">{summary}</p>
        </div>
      </div>
      <div className="flex gap-2">
        <button
          id={`confirm-action-${toolCall.id}`}
          onClick={handleConfirm}
          disabled={status === 'confirming'}
          className={cn(
            'flex-1 rounded-lg px-3 py-1.5 text-xs font-medium transition-all duration-200',
            'bg-emerald-600 text-white hover:bg-emerald-700 active:scale-[0.98]',
            'disabled:opacity-60 disabled:cursor-not-allowed',
          )}
        >
          {status === 'confirming' ? (
            <span className="flex items-center justify-center gap-1.5">
              <Icons.loader className="h-3 w-3 animate-spin" />
              Processing...
            </span>
          ) : (
            <span className="flex items-center justify-center gap-1.5">
              <Icons.check className="h-3 w-3" />
              Confirm
            </span>
          )}
        </button>
        <button
          id={`cancel-action-${toolCall.id}`}
          onClick={handleCancel}
          disabled={status === 'confirming'}
          className={cn(
            'flex-1 rounded-lg px-3 py-1.5 text-xs font-medium transition-all duration-200',
            'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50 active:scale-[0.98]',
            'disabled:opacity-60 disabled:cursor-not-allowed',
          )}
        >
          <span className="flex items-center justify-center gap-1.5">
            <Icons.close className="h-3 w-3" />
            Cancel
          </span>
        </button>
      </div>
    </div>
  )
}


// ============================================================================
// Main ChatMessage Component
// ============================================================================

export function ChatMessage({ message, userName, userImage }: ChatMessageProps) {
  const isUser = message.role === 'user'
  const isLoading = message.isLoading

  // Find mutation tool calls that need confirmation
  const mutationToolCalls = message.toolCalls?.filter(tc => {
    if (!MUTATION_TOOLS.has(tc.name)) return false
    const r = tc.result as Record<string, unknown> | undefined
    return r && r.requires_confirmation === true
  }) || []

  return (
    <div
      className={cn(
        'flex gap-3',
        isUser ? 'flex-row-reverse' : 'flex-row'
      )}
    >
      {/* Avatar */}
      <div className="flex-shrink-0">
        {isUser ? (
          <Avatar
            src={userImage}
            fallback={userName || 'U'}
            size="sm"
          />
        ) : (
          <div className={cn(
            'flex h-8 w-8 items-center justify-center rounded-full',
            'bg-gradient-to-br from-brand-navy to-brand-purple',
            'shadow-soft'
          )}>
            <Icons.sparkles className="h-4 w-4 text-white" strokeWidth={1.5} />
          </div>
        )}
      </div>

      {/* Message Content */}
      <div className={cn(
        'flex max-w-[85%] flex-col gap-1',
        isUser ? 'items-end' : 'items-start'
      )}>
        <div
          className={cn(
            'rounded-2xl px-4 py-3',
            'transition-all duration-200',
            isUser
              ? 'bg-brand-navy text-white rounded-br-md'
              : 'bg-white/90 text-foreground border border-border/50 rounded-bl-md shadow-soft',
            isLoading && 'animate-pulse'
          )}
        >
          {isLoading ? (
            <div className="flex items-center gap-2 py-1">
              <div className="flex gap-1">
                <span className="h-2 w-2 animate-bounce rounded-full bg-brand-muted/60" style={{ animationDelay: '0ms' }} />
                <span className="h-2 w-2 animate-bounce rounded-full bg-brand-muted/60" style={{ animationDelay: '150ms' }} />
                <span className="h-2 w-2 animate-bounce rounded-full bg-brand-muted/60" style={{ animationDelay: '300ms' }} />
              </div>
              <span className="text-sm text-brand-muted">Thinking...</span>
            </div>
          ) : isUser ? (
            <p className="text-sm whitespace-pre-wrap leading-relaxed">
              {message.content}
            </p>
          ) : (
            <MarkdownContent content={message.content} />
          )}

          {/* Confirmation Cards for mutation tool calls */}
          {mutationToolCalls.map(tc => (
            <ConfirmationCard key={tc.id} toolCall={tc} />
          ))}
        </div>

        {/* Timestamp */}
        <span className={cn(
          'text-[10px] text-muted-foreground/60',
          isUser ? 'pr-1' : 'pl-1'
        )}>
          {formatTime(message.timestamp)}
        </span>

        {/* Tool calls — download links, navigation, and indicators */}
        {message.toolCalls && message.toolCalls.length > 0 && (
          <div className="flex flex-col gap-1.5 pl-1 mt-1">
            {message.toolCalls.map((tc) => {
              const result = tc.result as Record<string, unknown> | undefined

              // Report download button (authenticated blob download — bradesco pattern)
              if (tc.name === 'generate_report' && result?.download_url) {
                const handleDownload = async () => {
                  try {
                    const { getSession } = await import('next-auth/react')
                    const session = await getSession()
                    const apiUrl = process.env.NEXT_PUBLIC_API_URL || 'http://localhost:8001'
                    const basePath = process.env.NEXT_PUBLIC_BASE_PATH || ''
                    const downloadUrl = `${apiUrl}${basePath}${result.download_url}`

                    const response = await fetch(downloadUrl, {
                      headers: session?.accessToken
                        ? { Authorization: `Bearer ${session.accessToken}` }
                        : {},
                    })

                    if (!response.ok) throw new Error(`Download failed: ${response.status}`)

                    const blob = await response.blob()
                    const url = URL.createObjectURL(blob)
                    const a = document.createElement('a')
                    a.href = url
                    a.download = String(result.filename || `report.${result.format || 'pdf'}`)
                    document.body.appendChild(a)
                    a.click()
                    document.body.removeChild(a)
                    URL.revokeObjectURL(url)
                  } catch {
                    // Fallback: open in new tab (may still fail without auth)
                    window.open(
                      `${process.env.NEXT_PUBLIC_API_URL || 'http://localhost:8001'}${
                        process.env.NEXT_PUBLIC_BASE_PATH || ''
                      }${result.download_url}`,
                      '_blank',
                    )
                  }
                }
                return (
                  <button
                    key={tc.id}
                    type="button"
                    onClick={handleDownload}
                    className={cn(
                      'inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-medium',
                      'bg-gradient-to-r from-brand-navy to-brand-purple',
                      'text-white shadow-md hover:shadow-lg',
                      'transition-all duration-200 hover:scale-[1.02] active:scale-[0.98] cursor-pointer',
                    )}
                  >
                    <Icons.download className="h-3.5 w-3.5" strokeWidth={2} />
                    Download {String(result.format || 'PDF').toUpperCase()} Report
                    {result.file_size_kb != null && (
                      <span className="text-white/60 text-[10px]">({String(result.file_size_kb)} KB)</span>
                    )}
                  </button>
                )
              }

              // Navigate action
              if (tc.name === 'navigate_user' && result?.target_page) {
                return (
                  <a
                    key={tc.id}
                    href={String(result.target_page)}
                    className={cn(
                      'inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-medium',
                      'bg-brand-purple/10 text-brand-purple border border-brand-purple/20',
                      'hover:bg-brand-purple/20 transition-colors',
                    )}
                  >
                    <Icons.externalLink className="h-3.5 w-3.5" strokeWidth={1.5} />
                    Go to {String(result.page_name || result.target_page)}
                  </a>
                )
              }

              // Skip mutation tools (handled by ConfirmationCard above)
              if (MUTATION_TOOLS.has(tc.name)) return null

              // Default: subtle tool indicator
              return (
                <div key={tc.id} className="flex items-center gap-1.5 text-[10px] text-brand-cornflower">
                  <Icons.zap className="h-3 w-3" strokeWidth={1.5} />
                  <span>{tc.name.replace(/_/g, ' ')}</span>
                </div>
              )
            })}
          </div>
        )}
      </div>
    </div>
  )
}
